import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Smartphone } from "lucide-react";

/**
 * Android-only one-tap PWA install banner.
 *
 * Behaviour:
 * - Shows ONLY on Android devices that fire the `beforeinstallprompt` event
 *   (Chrome / Edge / Samsung Internet / Brave on Android).
 * - One tap on the CTA → triggers the native install dialog directly
 *   (no manual "menu → add to home" steps).
 * - iOS, Desktop, and unsupported browsers see nothing here. iOS instructions
 *   are handled separately on the dedicated /app page.
 * - Remembers user choice (installed / dismissed) in localStorage; respects
 *   it for 30 days so we don't nag returning visitors.
 *
 * The native browser install card on Android is a small, easy-to-miss UI
 * affordance buried in the address bar. This component surfaces it as a
 * full-width sticky banner that *cannot* be missed by non-tech users.
 */
const KEY = "vdh_pwa_install_state_v2";
const COOLDOWN_DAYS = 30;

function isAndroid() {
  return /Android/i.test(navigator.userAgent);
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true ||
    document.referrer.startsWith("android-app://")
  );
}

function shouldShow() {
  if (typeof window === "undefined") return false;
  if (isStandalone()) return false; // already installed
  if (isIOS()) return false; // iOS handled elsewhere
  if (!isAndroid()) return false; // Android only

  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return true;
    const data = JSON.parse(raw);
    if (data.state === "installed") return false;
    if (data.state === "dismissed" && data.ts) {
      const elapsed = (Date.now() - data.ts) / (1000 * 60 * 60 * 24);
      return elapsed > COOLDOWN_DAYS;
    }
    return true;
  } catch {
    return true;
  }
}

function persist(state) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ state, ts: Date.now() }));
  } catch {
    /* noop */
  }
}

export default function PWAInstallBanner() {
  const [visible, setVisible] = useState(false);
  const [installing, setInstalling] = useState(false);
  const deferredPromptRef = useRef(null);

  useEffect(() => {
    if (!shouldShow()) return;

    const onBeforeInstall = (e) => {
      // Capture the event so we can trigger the native prompt later
      e.preventDefault();
      deferredPromptRef.current = e;
      // Brief delay so the banner doesn't appear during initial paint
      setTimeout(() => setVisible(true), 1200);
    };

    const onInstalled = () => {
      persist("installed");
      setVisible(false);
      deferredPromptRef.current = null;
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    const ev = deferredPromptRef.current;
    if (!ev || installing) return;
    setInstalling(true);
    try {
      ev.prompt();
      const { outcome } = await ev.userChoice;
      if (outcome === "accepted") {
        persist("installed");
      } else {
        persist("dismissed");
      }
    } catch {
      persist("dismissed");
    } finally {
      deferredPromptRef.current = null;
      setInstalling(false);
      setVisible(false);
    }
  };

  const dismiss = () => {
    persist("dismissed");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 120, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 120, opacity: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        className="fixed bottom-3 inset-x-3 md:hidden z-[100] vdh-safe-bottom"
        data-testid="pwa-install-banner"
      >
        <div
          className="relative rounded-3xl p-4 backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(234,88,12,0.35),0_12px_40px_rgba(0,0,0,0.6)]"
          style={{
            background:
              "linear-gradient(135deg, rgba(255,79,207,0.16) 0%, rgba(13,13,15,0.96) 60%)",
            border: "1px solid rgba(234,88,12,0.35)",
          }}
        >
          <button
            type="button"
            onClick={dismiss}
            aria-label="Chiudi banner"
            className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/55 hover:text-white transition-colors"
            data-testid="pwa-dismiss"
          >
            <X size={14} />
          </button>

          <div className="flex items-center gap-3 mb-3">
            <div className="relative w-12 h-12 rounded-2xl flex-none flex items-center justify-center bg-gradient-to-br from-[var(--vdh-pink)] to-[var(--vdh-yellow)] text-black">
              <Smartphone size={22} />
              {/* Pulse */}
              <span
                aria-hidden
                className="absolute inset-0 rounded-2xl animate-ping opacity-50"
                style={{
                  background:
                    "radial-gradient(circle, rgba(234,88,12,0.45) 0%, transparent 70%)",
                }}
              />
            </div>
            <div className="min-w-0">
              <div className="font-display text-[17px] leading-tight tracking-tight text-white">
                Installa l'app
              </div>
              <div className="text-[11.5px] text-white/65 font-tech">
                Vita da Host sulla tua home, in 1 tocco
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={install}
            disabled={installing}
            data-testid="pwa-install-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-[14px] font-semibold text-black bg-gradient-to-b from-[var(--vdh-yellow)] to-orange-600 ring-1 ring-orange-300/60 active:scale-[0.98] transition-transform disabled:opacity-60"
          >
            <Download size={15} strokeWidth={2.4} />
            {installing ? "Apertura…" : "Installa ora"}
          </button>

          <div className="text-center text-[10.5px] text-white/35 mt-2.5 font-tech tracking-wide">
            Gratis · Niente Play Store · Si chiude in 1 tap
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
